import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';
import HubPageLayout from '../components/HubPageLayout';
import { BackToTopLink } from '../components/BackToTop';
import HeroBanner from './components/HeroBanner';
import AlertSourceTabs from './components/AlertSourceTabs';
import AlertsTable from './components/AlertsTable';
import { alertsEndpoint, ALERT_SOURCES } from './alertSources';
import { normalizeAlert, isNew, fmtDotDate } from './alertHelpers';

const TAB_IDS = [...ALERT_SOURCES.map((s) => s.id), 'hot-scams'];

// Fraud Alerts hub — the dashboard landing page. Agency tabs read
// DB-published alerts; the Hot Scams tab reads the hot_scams content pages.
const FraudAlertsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [alertsBySource, setAlertsBySource] = useState({});

  // Keeping the tab in the URL means Back from a Hot Scams detail page lands
  // on the Hot Scams tab again instead of resetting to FBI/IC3.
  const tabParam = searchParams.get('tab');
  const activeSource = TAB_IDS.includes(tabParam) ? tabParam : 'ic3';
  const setActiveSource = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'ic3') next.delete('tab');
    else next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    const put = (id, value) => {
      if (!cancelled) setAlertsBySource((prev) => ({ ...prev, [id]: value }));
    };

    ALERT_SOURCES.forEach(({ id }) => {
      fetch(`${API_BASE_URL}${alertsEndpoint(id)}`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((d) => {
          const rows = (d.items || [])
            .map((a) => ({ ...normalizeAlert(a), to: `/dashboard/fraud-alerts/${a.id}` }))
            .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
          put(id, { rows, loading: false, error: false });
        })
        .catch(() => put(id, { rows: [], loading: false, error: true }));
    });

    fetch(`${API_BASE_URL}/bff/hot-scams`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        const rows = (d.items || []).map((s) => ({
          id: s.id,
          title: s.title,
          dateLabel: fmtDotDate(s.published_at),
          isNew: isNew(s.published_at),
          // Per-user read marker from hot_scam_views (10-06), same as alerts.
          viewed: Boolean(s.viewed),
          to: `/dashboard/hot-scams/${s.slug}`,
        }));
        put('hot-scams', { rows, loading: false, error: false });
      })
      .catch(() => put('hot-scams', { rows: [], loading: false, error: true }));

    return () => { cancelled = true; };
  }, []);

  const current = alertsBySource[activeSource] || { rows: [], loading: true, error: false };
  const newBySource = Object.fromEntries(
    TAB_IDS.map((id) => [id, (alertsBySource[id]?.rows || []).some((r) => r.isNew)])
  );

  return (
    <HubPageLayout>
      <HeroBanner />
      <AlertSourceTabs
        activeSource={activeSource}
        onSelect={setActiveSource}
        newBySource={newBySource}
      />
      <AlertsTable
        rows={current.rows}
        loading={current.loading}
        error={current.error}
        agencyTab={activeSource !== 'hot-scams'}
      />
      <BackToTopLink />
    </HubPageLayout>
  );
};

export default FraudAlertsPage;
